import { type CSSProperties, type ReactNode, useState } from "react";
import { Badge, Button, Group, Loader, Paper, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import type { InterruptionCause, PlaybackState } from "./playback";
import { useMonitor } from "./useMonitor";
import styles from "./Monitor.module.scss";

/**
 * What the room is seeing, on the panel (`R-VID-9`, `R-HOST-19`).
 *
 * > *The host is shown what viewers are actually seeing, including the true delay, so they can
 * > pace their commentary against it.*
 *
 * ## Its own card, beside the broadcast one rather than inside it
 *
 * The two answer different questions and belong to different people. `Broadcast` is *am I
 * publishing, and is my camera any good* — it is the presenter's, and under spec §3's two roles
 * it is the **host's** alone. This is *what is the room getting, and how far behind is it*, which
 * is everybody's: a second host driving lots from a laptop and a moderator reading comments both
 * want it, and a moderator cannot see the broadcast card at all.
 *
 * Folding it into `Broadcast` would have meant opening that card — and the pre-flight route
 * behind it, which reports on the lot list — to a role that has no business in either.
 *
 * ## Never for the person broadcasting
 *
 * `ShowPage` withholds it while this browser is publishing. Their camera preview is the same
 * picture a few hundred milliseconds earlier, subscribing would cost a participant slot and some
 * CPU on a device that is already encoding, and unmuting it beside the microphone it came from is
 * a feedback loop. The presenter's copy of `R-VID-9` is the delay figure, and that is the one
 * thing here they genuinely lack — worth revisiting, and not by giving them a second video.
 *
 * ## Muted, and it says why
 *
 * The room makes unmuting a primary control (`R-VID-13`) because the host's voice is the sale.
 * Here it is the opposite: the likeliest second screen is a laptop in the same room as the
 * microphone, so sound is off until asked for and the button carries the warning rather than a
 * help text nobody reads.
 */
export function Monitor({
  publicId,
  live,
  turns,
  presenting = false,
  overlay,
  onPresent,
}: {
  publicId: string;
  live: boolean;
  /**
   * Presenter mode (`presenter/PresenterOverlay.tsx`): the picture fills the screen and `overlay`
   * is drawn over it. The same video element, restyled, rather than a second one — so nothing is
   * re-attached, re-subscribed or re-published by entering or leaving it.
   */
  presenting?: boolean;
  overlay?: ReactNode;
  /** Opens presenter mode over this monitor — for a host presenting from a second device. */
  onPresent?: () => void;

  /**
   * Quarter turns clockwise (`R-VID-8`), from the show — the same value the room applies.
   *
   * Without it a panel watching a show shot on a camera mounted sideways would see a sideways
   * picture, decide the show was broken, and be wrong: every viewer is seeing it upright.
   */
  turns: number;
}) {
  const { t } = useTranslation();
  /**
   * The stream's shape, once the browser has decoded enough to know it (`R-VID-8`).
   *
   * Undefined until then, which is what the stylesheet's fallback covers. A plain number
   * (width ÷ height) because the stylesheet needs it twice — once as `aspect-ratio` and once
   * inside a `calc` that caps the width — and only a number works in both.
   */
  const [aspect, setAspect] = useState<number | undefined>(undefined);
  /**
   * Destructured rather than kept as one object, and it is not a style preference: passing any
   * property of an object to a `ref` prop marks the whole object as ref-like to React's own lint
   * rules, and every other property read of it during render then reports as a ref access.
   * `Broadcast` takes the pieces out for the same reason.
   */
  const {
    videoRef,
    state,
    cause,
    muted,
    unmute,
    mute,
    latencyMs,
    failure,
    givenUp,
    retry,
  } = useMonitor(publicId, live);

  return (
    <Paper withBorder radius="lg" p="lg" className={styles.card}>
      <Stack gap="sm">
        <Group justify="space-between" align="center" wrap="wrap">
          <Group gap="sm" align="center">
            <Text fw={650} size="sm">
              {t("monitor.heading")}
            </Text>
            {/*
              The delay, which is the requirement rather than a decoration (`R-VID-9`). It is the
              jitter buffer plus half the round trip — **not** glass-to-glass, so the label says
              *to you* rather than claiming to be the figure in the specification. A host pacing
              "going once" against it is the whole reason it is on screen.
            */}
            {latencyMs === undefined ? null : (
              <Badge variant="light" color="ink" size="sm" radius="sm">
                {t("monitor.delay", { ms: latencyMs })}
              </Badge>
            )}
          </Group>
          <Group gap="xs" wrap="nowrap">
            {live && onPresent ? (
              <Button size="compact-xs" variant="default" onClick={onPresent}>
                {t("presenter.enter")}
              </Button>
            ) : null}
            <Button
              size="compact-xs"
              variant={muted ? "default" : "filled"}
              onClick={() => (muted ? unmute() : mute())}
            >
              {t(muted ? "monitor.unmute" : "monitor.mute")}
            </Button>
          </Group>
        </Group>

        {/*
          The frame is given the stream's own shape, and that is why this is not pure CSS.

          A box sized from the picture and a picture capped by the box are a circular
          constraint, and a browser resolves it by guessing at one of them — letterboxing a
          portrait stream into a landscape box, or cropping it. Neither is recoverable from in a
          stylesheet, because the fact that settles it — the stream's
          aspect ratio — exists only at runtime.

          So it is read off the element the moment the browser knows it, handed to CSS as a
          custom property, and the frame is sized `height × aspect`. Until then the stylesheet's
          fallback is `R-VID-8`'s primary format, so the common case never reshapes at all.
        */}
        <div
          className={styles.frame}
          data-presenting={presenting || undefined}
          style={
            aspect ? ({ "--monitor-ar": String(aspect) } as CSSProperties) : undefined
          }
        >
          <video
            ref={videoRef}
            className={styles.video}
            data-turns={turns === 0 ? undefined : turns}
            /*
              `videoWidth`/`videoHeight` are the decoded frame's, so this is the shape a viewer
              is getting rather than anything the panel chose. It fires again if the publisher
              switches camera mid-show, which a one-shot read would get wrong.
            */
            onLoadedMetadata={(event) => {
              const { videoWidth, videoHeight } = event.currentTarget;
              if (videoWidth <= 0 || videoHeight <= 0) return;
              /*
                A quarter turn swaps what the viewer sees without changing the element's layout
                box, so a turned stream is given a square to turn inside — the one shape a
                quarter turn maps onto itself. `object-fit` keeps the picture whole in it.
              */
              const square = turns === 1 || turns === 3;
              setAspect(square ? 1 : videoWidth / videoHeight);
            }}
            muted
            autoPlay
            playsInline
          />
          <PlaybackState
            state={state}
            cause={cause}
            givenUp={givenUp}
            onRetry={retry}
          />
          {presenting ? overlay : null}
        </div>

        {/*
          Said once, under the picture, rather than as a tooltip on the button. Somebody who
          unmutes a monitor next to a live microphone finds out immediately and loudly; a line of
          text beforehand is cheaper than the alternative.
        */}
        <Text size="xs" c="dimmed">
          {t("monitor.help")}
        </Text>

        {failure ? (
          <Text size="xs" c="orange">
            {failure}
          </Text>
        ) : null}
      </Stack>
    </Paper>
  );
}

/**
 * The overlay, and which states get one.
 *
 * `playing` draws nothing — the picture is the state. `buffering` is deliberately quiet, a corner
 * label over a picture that is still there, because most stalls recover unnoticed and a panel
 * that shouted about each one would be unreadable during an ordinary show (`R-VID-12`).
 *
 * `interrupted` is loud, and it **names whose end failed** (`R-VID-17`). That distinction is the
 * entire value of this card during a bad moment: *the stream has stopped* sends somebody to check
 * on the presenter, and *this browser lost its connection* does not. Where the browser genuinely
 * cannot tell, it says so rather than guessing.
 */
function PlaybackState({
  state,
  cause,
  givenUp,
  onRetry,
}: {
  state: PlaybackState;
  cause: InterruptionCause | undefined;
  givenUp: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation();

  if (givenUp) {
    return (
      <div className={styles.state}>
        <Text size="sm" fw={600}>
          {t("monitor.givenUp")}
        </Text>
        <Button size="compact-xs" variant="white" onClick={onRetry}>
          {t("monitor.retry")}
        </Button>
      </div>
    );
  }

  if (state === "connecting") {
    return (
      <div className={styles.state}>
        <Loader size="sm" color="gray.3" />
        <Text size="xs">{t("monitor.connecting")}</Text>
      </div>
    );
  }

  if (state === "buffering") {
    return (
      <div className={styles.state} data-quiet>
        <Text size="xs">{t("monitor.buffering")}</Text>
      </div>
    );
  }

  if (state === "interrupted") {
    return (
      <div className={styles.state}>
        <Text size="sm" fw={600}>
          {t(`monitor.interrupted.${cause ?? "unknown"}`)}
        </Text>
        <Text size="xs">{t(`monitor.advice.${cause ?? "unknown"}`)}</Text>
      </div>
    );
  }

  return null;
}
